import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import creatorImage from '../images/team/ali-yousefi.jpg';
import '../styles/about-team.css';

const AboutPage = () => {
  return (
    <div className="page">
      <Header />

      {/* Hero Section */}
      <section className="about-hero-section">
        <div className="about-hero-content">
          <h1 className="about-hero-title">
            About <span className="brand-highlight">Patients Empowered</span>
          </h1>
          <p className="about-hero-subtitle">
            Revolutionizing healthcare through AI-powered consultations that are accessible, accurate, and personalized.
          </p>
        </div>
      </section>

      {/* Meet the Creator Section */}
      <section className="founder-section">
        <div className="founder-section-inner">
          <div className="founder-card">
            <div className="founder-left">
              <div className="founder-photo-wrapper">
                <img src={creatorImage} alt="Ali Yousefli" className="founder-photo" />
              </div>
              <h3 className="founder-name">Ali Yousefli</h3>
              <p className="founder-credential">Dartmouth MBA/MPH Graduate</p>
              <div className="founder-links">
                <a href="https://www.linkedin.com/in/aysf/" target="_blank" rel="noopener noreferrer" className="founder-link-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
              </div>
            </div>

            <div className="founder-right">
              <h2 className="founder-section-title">Meet the Creator</h2>
              <p>
                Ali Yousefli is a product manager with 7+ years of experience spanning AI product development, pharmaceutical R&D, and Fortune 15 quality and operations. He holds a dual MBA/MPH from Dartmouth (Tuck School of Business and Geisel School of Medicine) and is passionate about applying AI to solve real world problems in healthcare.
              </p>
              <p>
                Ali is currently a product manager at a national telehealth provider, where he works on improving treatment adherence in the critical first month of care and translating clinical research and outcomes data into product strategy. He also designed and launched a closed loop incident reporting and resolution system and built an internal clinical operations platform from scratch. Before that, he was an AI product manager at a strategy consultancy, owning the full product lifecycle for 14+ AI tools, and an AI scientist at a precision medicine startup, where he developed a dual LLM architecture with retrieval augmented generation for personalized patient and clinician recommendations.
              </p>
              <p>
                Ali created Patients Empowered from his experience observing gaps in healthcare access and his belief that AI can democratize medical expertise while supporting healthcare providers in clinical decision making.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Education & Experience Section */}
      <section className="credentials-section">
        <div className="credentials-inner">
          <div className="credentials-grid">
            {/* Education Card */}
            <div className="credential-card">
              <div className="credential-header">
                <span className="credential-icon education-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.657 2.686 3 6 3s6-1.343 6-3v-5"/></svg>
                </span>
                <h3>Education</h3>
              </div>
              <div className="credential-body">
                <div className="credential-item">
                  <h4>Tuck School of Business at Dartmouth</h4>
                  <p className="credential-degree">MBA, 2023-2025</p>
                  <p className="credential-detail">Merit-based Tuck Scholarship Recipient; Health Care Club, Tuck Center for Health Care Fellow</p>
                </div>
                <div className="credential-item">
                  <h4>Geisel School of Medicine at Dartmouth</h4>
                  <p className="credential-degree">MPH, 2024-2025</p>
                </div>
                <div className="credential-item">
                  <h4>University of Manitoba</h4>
                  <p className="credential-degree">Bachelor of Science (Biology, Chemistry), 2013-2017</p>
                  <p className="credential-detail">Research Projects: In-vitro assays of snake venom metalloproteases | Histological studies on mice myocardium</p>
                </div>
              </div>
            </div>

            {/* Professional Experience Card */}
            <div className="credential-card">
              <div className="credential-header">
                <span className="credential-icon experience-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                </span>
                <h3>Professional Experience</h3>
              </div>
              <div className="credential-body">
                <div className="credential-item">
                  <h4>Aytza</h4>
                  <p className="credential-degree">AI Product Manager & Chief of Staff (2025-2026)</p>
                  <p className="credential-detail">Led the full product lifecycle for AI tools including autonomous news agents, proposal writers, and competitive intelligence systems. Automated complex consulting workflows using LLM evals, RAG infrastructure, and human-in-the-loop feedback.</p>
                </div>
                <div className="credential-item">
                  <h4>Molecular You</h4>
                  <p className="credential-degree">AI Scientist & Strategy Associate (2024-2025)</p>
                  <p className="credential-detail">Developed a novel AI system with dual-LLM architecture and RAG for personalized patient and clinician recommendations, hosted on-premise to protect patient data.</p>
                </div>
                <div className="credential-item">
                  <h4>Cardinal Health</h4>
                  <p className="credential-degree">Senior Specialist, Quality Assurance (2020-2023)</p>
                  <p className="credential-detail">Led Kaizen events delivering significant cost savings by streamlining U.S. manufacturing processes. Directed temperature management across all Canadian warehouses.</p>
                </div>
                <div className="credential-item">
                  <h4>Apotex Inc.</h4>
                  <p className="credential-degree">Research Scientist, R&D Chemistry Department (2017-2019)</p>
                  <p className="credential-detail">Designed and executed experimental projects to advance drug development.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
